import React, { useEffect, useState } from "react";

import ShiftStream from "../..";

import Table from "./Table";
import Pagination from "../../components/pagination";

import Icon, { ICON_NAMES as Icons } from "~/src/components/icon";
import { useVenues } from "../../graphql/use-venues";
import { useClients } from "../../graphql/use-clients";
import { useRoles } from "../../graphql/use-roles";
import { useAccounts } from "../../graphql/use-accounts";
import { useFulfils } from "./graphql/use-fulfils";
import {
  FilterGrid,
  FilterGridRow,
  FilterSelect,
  FilterSlot,
  FooterControls,
  PaginationSlot,
  RefreshButton,
} from "../styles";
import { getProviderLabel } from "../../utils";

export const COL_WIDTHS = [
  "9vw",
  "13vw",
  "13vw",
  "12vw",
  "10vw",
  "13vw",
  "10vw",
  "7vw",
];

export const Fulfils = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [accountFilter, setAccountFilter] = useState<number | "all">("all");
  const [clientFilter, setClientFilter] = useState<number | "all">("all");
  const [roleFilter, setRoleFilter] = useState<number | "all">("all");
  const [stateFilter, setStateFilter] = useState<string | "all">("all");
  const [venueFilter, setVenueFilter] = useState<number | "all">("all");

  useEffect(() => {
    setCurrentPage(1);
  }, [accountFilter, clientFilter, roleFilter, venueFilter, stateFilter]);

  useEffect(() => {
    setVenueFilter("all");
    setRoleFilter("all");
  }, [clientFilter]);

  const { accounts } = useAccounts();

  const { clients } = useClients();

  const { roles } = useRoles({
    clientIds: clientFilter !== "all" ? [clientFilter] : null,
  });

  const { venues } = useVenues({
    clientIds: clientFilter !== "all" ? [clientFilter] : null,
  });

  const { fulfils, totalPages, loading, refetch } = useFulfils(
    {
      accountId: accountFilter !== "all" ? accountFilter : null,
      sourceAccountId: clientFilter !== "all" ? clientFilter : null,
      roleId: roleFilter !== "all" ? roleFilter : null,
      venueId: venueFilter !== "all" ? venueFilter : null,
      state: stateFilter !== "all" ? stateFilter : null,
    },
    currentPage,
  );

  useEffect(() => {
    if (!loading && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, loading]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    const scrollBody = document.querySelector(".streams-table-scroll-body");
    if (scrollBody instanceof HTMLElement) {
      scrollBody.scrollTo({ top: 0, behavior: "auto" });
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <ShiftStream>
      <FilterGrid>
        <FilterGridRow colWidths={COL_WIDTHS}>
          <FilterSlot>
            <FilterSelect
              id="stream-account-filter"
              value={accountFilter ?? "all"}
              onChange={(e) =>
                setAccountFilter(
                  e.target.value === "all" ? "all" : Number(e.target.value),
                )
              }
            >
              <option value="all">All Accts</option>
              {accounts.map((account: any) => (
                <option key={account.id} value={account.id}>
                  {account.name} ({getProviderLabel(account.provider)})
                </option>
              ))}
            </FilterSelect>
          </FilterSlot>

          <FilterSlot>
            <FilterSelect
              id="stream-client-filter"
              value={clientFilter ?? "all"}
              onChange={(e) =>
                setClientFilter(
                  e.target.value === "all" ? "all" : Number(e.target.value),
                )
              }
            >
              <option value="all">All Clients</option>
              {clients.map((client: any) => (
                <option key={client.id} value={client.id}>
                  {client.accountName}
                </option>
              ))}
            </FilterSelect>
          </FilterSlot>

          <FilterSlot>
            <FilterSelect
              id="stream-venue-filter"
              value={venueFilter ?? "all"}
              onChange={(e) =>
                setVenueFilter(
                  e.target.value === "all" ? "all" : Number(e.target.value),
                )
              }
            >
              <option value="all">All Venues</option>
              {venues.map((venue: any) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </FilterSelect>
          </FilterSlot>

          <FilterSlot>
            <FilterSelect
              id="stream-role-filter"
              value={roleFilter ?? "all"}
              onChange={(e) =>
                setRoleFilter(
                  e.target.value === "all" ? "all" : Number(e.target.value),
                )
              }
            >
              <option value="all">All Roles</option>
              {roles.map((role: any) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </FilterSelect>
          </FilterSlot>

          <FilterSlot>
            <RefreshButton
              type="button"
              aria-label="Refresh"
              title="Refresh"
              disabled={loading}
              onClick={handleRefresh}
              style={{ marginLeft: 0 }}
            >
              <Icon
                name={Icons.REFRESH}
                size={18}
                color={
                  loading ? Icon.colors.GREY_DARK : Icon.colors.MAIN_ORANGE
                }
                onClick={handleRefresh}
              />
            </RefreshButton>
          </FilterSlot>

          <FilterSlot />
          <FilterSlot />

          <FilterSlot style={{ justifyContent: "flex-end" }}>
            <FilterSelect
              id="stream-state-filter"
              value={stateFilter ?? "all"}
              onChange={(e) =>
                setStateFilter(
                  e.target.value === "all" ? "all" : e.target.value,
                )
              }
            >
              <option value="all">Any</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="WON">Won</option>
              <option value="LOST">Lost</option>
              <option value="FAILED">Failed</option>
            </FilterSelect>
          </FilterSlot>
        </FilterGridRow>
      </FilterGrid>

      <Table fulfils={fulfils} loading={loading} />

      <FooterControls>
        <PaginationSlot>
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            disabled={loading}
          />
        </PaginationSlot>
      </FooterControls>
    </ShiftStream>
  );
};
